import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BookOpen, ArrowRight } from 'lucide-react';

// Service ID → Blog slug mapping
const serviceBlogMap = {
  "residential-painting": "complete-guide-residential-painting-dallas",
  "remodeling-renovations": "kitchen-bathroom-remodeling-dallas-complete-guide",
  "drywall-repair": "drywall-repair-installation-dallas-guide",
  "carpentry": "custom-carpentry-trim-work-dallas-guide",
  "tile-installation": "tile-installation-dallas-complete-guide",
  "exterior-siding": "exterior-siding-installation-dallas-guide",
  "deck-construction": "deck-construction-dallas-complete-guide",
  "mold-remediation": "mold-remediation-removal-dallas-guide",
  "parking-lot-striping": "parking-lot-striping-seal-coating-dallas-guide",
  "brick-masonry": "brick-masonry-services-dallas-guide",
  "roof-repairs": "roof-repair-leak-detection-dallas-guide"
};

// Blog slug → blog title (short, for anchor text)
const blogTitleMap = {
  "complete-guide-residential-painting-dallas": "Complete Guide to Residential Painting in Dallas",
  "kitchen-bathroom-remodeling-dallas-complete-guide": "Kitchen & Bathroom Remodeling Guide for Dallas Homeowners",
  "drywall-repair-installation-dallas-guide": "Drywall Repair & Installation Guide for Dallas",
  "custom-carpentry-trim-work-dallas-guide": "Custom Carpentry & Trim Work Guide for Dallas",
  "tile-installation-dallas-complete-guide": "Complete Tile Installation Guide for Dallas Homes",
  "exterior-siding-installation-dallas-guide": "Exterior Siding Installation Guide for Dallas",
  "deck-construction-dallas-complete-guide": "Deck Construction Guide for Dallas Homeowners",
  "mold-remediation-removal-dallas-guide": "Mold Remediation & Removal Guide for Dallas",
  "parking-lot-striping-seal-coating-dallas-guide": "Parking Lot Striping & Seal Coating Guide",
  "brick-masonry-services-dallas-guide": "Brick & Masonry Services Guide for Dallas",
  "roof-repair-leak-detection-dallas-guide": "Roof Repair & Leak Detection Guide for Dallas"
};

// All 15 cities
const ALL_CITIES = [
  { slug: "dallas", name: "Dallas" },
  { slug: "plano", name: "Plano" },
  { slug: "frisco", name: "Frisco" },
  { slug: "richardson", name: "Richardson" },
  { slug: "garland", name: "Garland" },
  { slug: "irving", name: "Irving" },
  { slug: "mesquite", name: "Mesquite" },
  { slug: "rockwall", name: "Rockwall" },
  { slug: "southlake", name: "Southlake" },
  { slug: "grapevine", name: "Grapevine" },
  { slug: "colleyville", name: "Colleyville" },
  { slug: "heath", name: "Heath" },
  { slug: "wylie", name: "Wylie" },
  { slug: "sachse", name: "Sachse" },
  { slug: "las-colinas", name: "Las Colinas" }
];

export const getBlogSlugForService = (serviceId) => serviceBlogMap[serviceId] || null;
export const getBlogTitleForService = (serviceId) => {
  const slug = serviceBlogMap[serviceId];
  return slug ? blogTitleMap[slug] : null;
};

/**
 * "Available in Your Area" city links grid for a given service
 */
export const CityLinksGrid = ({ serviceId, shortTitle }) => (
  <div data-testid="city-links-grid">
    <h2 className="text-2xl font-bold text-neutral-900 mb-4">
      {shortTitle} Services Available in Your Area
    </h2>
    <p className="text-neutral-600 mb-6">
      We provide professional {shortTitle.toLowerCase()} services throughout the Dallas-Fort Worth metroplex. Select your city for local pricing and details:
    </p>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
      {ALL_CITIES.map(c => (
        <Link
          key={c.slug}
          to={`/services/${serviceId}/${c.slug}`}
          className="flex items-center gap-2 px-4 py-3 bg-neutral-50 hover:bg-amber-50 hover:border-amber-300 border border-neutral-200 rounded-lg transition-colors group"
        >
          <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm font-medium text-neutral-700 group-hover:text-amber-600">{c.name}, TX</span>
        </Link>
      ))}
    </div>
  </div>
);

/**
 * Blog post link banner for a given service
 */
export const BlogLinkBanner = ({ serviceId }) => {
  const slug = serviceBlogMap[serviceId];
  const title = slug ? blogTitleMap[slug] : null;
  if (!slug || !title) return null;

  return (
    <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 flex items-start gap-4" data-testid="blog-link-banner">
      <BookOpen className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
      <div>
        <h3 className="font-bold text-neutral-900 mb-1">Read Our Expert Guide</h3>
        <Link
          to={`/blog/${slug}`}
          className="text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-1"
        >
          {title} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

/**
 * City links for blog posts — "Get This Service in Your Area"
 */
export const BlogCityLinks = ({ serviceId, category }) => (
  <div className="bg-neutral-50 rounded-xl p-6 mb-10" data-testid="blog-city-links">
    <h3 className="font-bold text-neutral-900 mb-3">Get {category} Services in Your Area</h3>
    <div className="flex flex-wrap gap-2">
      {ALL_CITIES.map(c => (
        <Link
          key={c.slug}
          to={`/services/${serviceId}/${c.slug}`}
          className="px-3 py-1.5 bg-white border border-neutral-200 hover:border-amber-400 hover:bg-amber-50 rounded-full text-sm text-neutral-700 hover:text-amber-600 transition-colors"
        >
          {c.name}
        </Link>
      ))}
    </div>
  </div>
);

export { ALL_CITIES };
